import type {
	AssistantModelMessage,
	JSONValue,
	ModelMessage,
	TextPart,
	ToolCallPart,
	ToolModelMessage,
	ToolResultPart,
	UserContent,
} from "ai";
import { buildUserMessageTextWithAttachments } from "./agent-chat-attachments";
import {
	exportViewportImageSummaryForModel,
	isExportViewportImageOutput,
} from "./agent-export-viewport-image";
import { AGENT_CHAT_MAX_HISTORY_MESSAGES } from "./board-agent-config";
import {
	generateImageInChatModelOutput,
	isGenerateImageInChatOutput,
} from "./generate-image-in-chat";
import type {
	AgentChatAssistantPart,
	AgentChatAttachment,
	AgentChatMessage,
	AgentChatToolCallPart,
	AgentChatUserMessage,
} from "./types";

function syntheticToolCallId(messageId: string, partIndex: number) {
	return `${messageId}:tool:${partIndex}`;
}

function toToolResultOutput(
	toolName: string,
	output: unknown,
): ToolResultPart["output"] {
	if (output === undefined || output === null) {
		return { type: "text", value: "" };
	}

	if (typeof output === "string") {
		return { type: "text", value: output };
	}

	if (
		toolName === "exportViewportImage" &&
		isExportViewportImageOutput(output)
	) {
		if (!output.exported || !output.base64) {
			return {
				type: "json",
				value: output as JSONValue,
			};
		}

		const summary = exportViewportImageSummaryForModel(output);

		return {
			type: "content",
			value: [
				{
					type: "text",
					text: JSON.stringify(summary),
				},
				{
					type: "file",
					data: { type: "data", data: output.base64 },
					mediaType: "image/png",
				},
			],
		};
	}

	// Chat images: never re-send pixels to the LLM (unlike board
	// exportViewportImage, which needs a vision file part).
	if (
		toolName === "generateImageInChat" &&
		isGenerateImageInChatOutput(output)
	) {
		return generateImageInChatModelOutput(output);
	}

	return {
		type: "json",
		value: output as JSONValue,
	};
}

function resolveToolCallId(
	parts: AgentChatAssistantPart[],
	partIndex: number,
	toolResult: Extract<AgentChatAssistantPart, { type: "tool-result" }>,
): string | null {
	if (toolResult.toolCallId) {
		return toolResult.toolCallId;
	}

	for (let index = partIndex - 1; index >= 0; index -= 1) {
		const part = parts[index];
		if (part?.type !== "tool-call") {
			continue;
		}

		if (part.toolName !== toolResult.toolName) {
			continue;
		}

		return part.toolCallId ?? syntheticToolCallId("assistant", index);
	}

	return null;
}

function collectCompletedToolPairs(parts: AgentChatAssistantPart[]) {
	const callsById = new Map<string, AgentChatToolCallPart>();
	const pairs: Array<{
		call: AgentChatToolCallPart;
		result: Extract<AgentChatAssistantPart, { type: "tool-result" }>;
		toolCallId: string;
	}> = [];

	for (const [index, part] of parts.entries()) {
		if (part.type === "tool-call") {
			const toolCallId =
				part.toolCallId ?? syntheticToolCallId("assistant", index);
			callsById.set(toolCallId, { ...part, toolCallId });
			continue;
		}

		if (part.type !== "tool-result") {
			continue;
		}

		const toolCallId = resolveToolCallId(parts, index, part);
		if (!toolCallId) {
			continue;
		}

		const call = callsById.get(toolCallId);
		if (!call) {
			continue;
		}

		pairs.push({ call, result: part, toolCallId });
		callsById.delete(toolCallId);
	}

	return pairs;
}

function assistantPartsToModelMessages(
	messageId: string,
	parts: AgentChatAssistantPart[],
): ModelMessage[] {
	const modelMessages: ModelMessage[] = [];
	const completedToolPairs = collectCompletedToolPairs(parts);
	const pairedToolCallIds = new Set(
		completedToolPairs.map((pair) => pair.toolCallId),
	);

	let assistantContent: Array<TextPart | ToolCallPart> = [];
	let pendingToolResults: ToolResultPart[] = [];

	const flushAssistant = () => {
		if (assistantContent.length === 0) {
			return;
		}

		modelMessages.push({
			role: "assistant",
			content: assistantContent,
		} satisfies AssistantModelMessage);
		assistantContent = [];
	};

	const flushToolResults = () => {
		if (pendingToolResults.length === 0) {
			return;
		}

		modelMessages.push({
			role: "tool",
			content: pendingToolResults,
		} satisfies ToolModelMessage);
		pendingToolResults = [];
	};

	const emitToolPair = (pair: (typeof completedToolPairs)[number]) => {
		flushAssistant();
		assistantContent.push({
			type: "tool-call",
			toolCallId: pair.toolCallId,
			toolName: pair.call.toolName,
			input: pair.call.input ?? {},
		});
		flushAssistant();
		pendingToolResults.push({
			type: "tool-result",
			toolCallId: pair.toolCallId,
			toolName: pair.result.toolName,
			output: toToolResultOutput(pair.result.toolName, pair.result.output),
		});
		flushToolResults();
	};

	for (const [partIndex, part] of parts.entries()) {
		switch (part.type) {
			case "text": {
				if (!part.text) {
					break;
				}

				flushToolResults();
				assistantContent.push({
					type: "text",
					text: part.text,
				});
				break;
			}
			case "generated-image": {
				flushToolResults();
				const label = part.modelLabel ?? part.modelId;
				if (part.status === "ready") {
					assistantContent.push({
						type: "text",
						text: `[Generated image ready: ${label}]`,
					});
				} else if (part.status === "error") {
					assistantContent.push({
						type: "text",
						text: `[Image generation failed: ${label}${
							part.errorMessage ? ` — ${part.errorMessage}` : ""
						}]`,
					});
				}
				break;
			}
			case "tool-call": {
				const toolCallId =
					part.toolCallId ?? syntheticToolCallId(messageId, partIndex);

				if (!pairedToolCallIds.has(toolCallId)) {
					break;
				}

				const pair = completedToolPairs.find(
					(candidate) => candidate.toolCallId === toolCallId,
				);
				if (pair) {
					emitToolPair(pair);
				}
				break;
			}
			case "tool-result":
				break;
			case "choice-request": {
				flushAssistant();
				flushToolResults();
				modelMessages.push({
					role: "assistant",
					content: [
						{
							type: "text",
							text: `${part.prompt}\nOptions: ${part.options
								.map((option) => `${option.label} (${option.id})`)
								.join(", ")}`,
						},
					],
				});
				break;
			}
			case "choice-response": {
				flushAssistant();
				flushToolResults();
				modelMessages.push({
					role: "user",
					content: `Selected: ${part.label} (${part.optionId})`,
				});
				break;
			}
			case "action-receipt": {
				assistantContent.push({
					type: "text",
					text: `${part.title}: ${part.description ?? part.status}`,
				});
				break;
			}
			default:
				break;
		}
	}

	flushAssistant();
	flushToolResults();

	return modelMessages;
}

function userMessageToModelContent(
	message: AgentChatUserMessage,
): string | UserContent {
	let text = buildUserMessageTextWithAttachments(
		message.content,
		message.attachments,
	);
	if (message.peopleContext) {
		text = text ? `${message.peopleContext}\n\n${text}` : message.peopleContext;
	}
	if (message.referenceContext) {
		text = text
			? `${message.referenceContext}\n\n${text}`
			: message.referenceContext;
	}
	const imageAttachments = (message.attachments ?? []).filter(
		(attachment): attachment is AgentChatAttachment & { kind: "image" } =>
			attachment.kind === "image",
	);

	if (imageAttachments.length === 0) {
		return text;
	}

	const parts: UserContent = [];
	if (text) {
		parts.push({ type: "text", text });
	}

	for (const attachment of imageAttachments) {
		parts.push({
			type: "file",
			data: { type: "data", data: attachment.data },
			mediaType: attachment.mediaType,
			filename: attachment.name,
		});
	}

	return parts;
}

export function agentChatMessagesToModelMessages(
	messages: AgentChatMessage[],
): ModelMessage[] {
	const recentMessages = messages.slice(-AGENT_CHAT_MAX_HISTORY_MESSAGES);
	const modelMessages: ModelMessage[] = [];

	for (const message of recentMessages) {
		if (message.role === "user") {
			modelMessages.push({
				role: "user",
				content: userMessageToModelContent(message),
			});
			continue;
		}

		modelMessages.push(
			...assistantPartsToModelMessages(message.id, message.parts),
		);
	}

	return modelMessages;
}
